import React, { useState, useEffect, useCallback } from 'react';
import api from '../config/axios';
import {
  ComposedChart, Line, BarChart, Bar, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, FunnelChart, Funnel, LabelList
} from 'recharts';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  primary:  '#667eea', success: '#10b981', danger:  '#ef4444',
  warning:  '#f59e0b', info:    '#3b82f6', purple:  '#8b5cf6',
  pink:     '#ec4899', meta:    '#1877f2', muted:   '#94a3b8',
  text:     '#1e293b', textSoft:'#64748b', bg:      '#f1f5f9',
  card:     '#ffffff', border:  '#e2e8f0',
  compare:  '#f59e0b', // cor do período de comparação nos gráficos
};
const CHART_COLORS = [C.primary, C.success, C.warning, C.danger, C.purple, C.pink, C.info, C.meta];

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtBRL  = v => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtNum  = v => v != null ? Number(v).toLocaleString('pt-BR') : '—';
const fmtPct  = v => v != null ? Number(v).toFixed(2) + '%' : '—';
const fmtRoas = v => v != null ? Number(v).toFixed(2) + 'x' : '—';

// ─── Date helpers (UTC-3 / America/Sao_Paulo) ─────────────────────────────────
const dtBR   = d => new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' }).format(d);
const todayBR    = () => dtBR(new Date());
const daysAgoBR  = n => { const d = new Date(); d.setDate(d.getDate() - n); return dtBR(d); };
const firstOfMonthBR = () => { const d = new Date(); d.setDate(1); return dtBR(d); };
const firstOfYearBR  = () => { const d = new Date(); d.setMonth(0, 1); return dtBR(d); };

function prevPeriod(from, to) {
  const f = new Date(from + 'T00:00:00-03:00');
  const t = new Date(to   + 'T23:59:59-03:00');
  const diff = t - f;
  const pTo   = new Date(f.getTime() - 86400000);
  const pFrom = new Date(pTo.getTime() - diff);
  return { from: dtBR(pFrom), to: dtBR(pTo) };
}

const PERIODS = [
  { label: 'Hoje',       from: () => todayBR(),       to: () => todayBR()       },
  { label: '7 dias',     from: () => daysAgoBR(7),    to: () => todayBR()       },
  { label: 'Este mês',   from: () => firstOfMonthBR(),to: () => todayBR()       },
  { label: '30 dias',    from: () => daysAgoBR(30),   to: () => todayBR()       },
  { label: '90 dias',    from: () => daysAgoBR(90),   to: () => todayBR()       },
  { label: 'Este ano',   from: () => firstOfYearBR(), to: () => todayBR()       },
  { label: 'Personalizado', from: null, to: null                                },
];

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function KpiCard({ title, value, compareValue, delta, color = C.primary, icon,
  formatter = fmtBRL, subvalue, alert, compareLabel }) {
  const isPos = delta >= 0;
  return (
    <div style={{ ...S.card, borderTop: `3px solid ${color}`, position: 'relative', padding: '14px 16px' }}>
      {alert && <span style={S.alertBadge}>⚠ Atenção</span>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <p style={S.kpiLabel}>{title}</p>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <p style={{ ...S.kpiValue, color }}>{formatter(value)}</p>
      {subvalue && <p style={S.kpiSub}>{subvalue}</p>}
      {compareValue != null && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, background: C.compare + '20', color: C.compare,
            padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>
            {compareLabel || 'Comparação'}: {formatter(compareValue)}
          </span>
          {delta != null && (
            <span style={{ fontSize: 11, fontWeight: 700, color: isPos ? C.success : C.danger }}>
              {isPos ? '▲' : '▼'} {Math.abs(delta)}%
            </span>
          )}
        </div>
      )}
      {compareValue == null && delta != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: isPos ? C.success : C.danger }}>
            {isPos ? '▲' : '▼'} {Math.abs(delta)}%
          </span>
          <span style={{ fontSize: 10, color: C.muted }}>vs período anterior</span>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon, children, accent = C.primary, action }) {
  return (
    <div style={S.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ ...S.sectionTitle, borderLeft: `3px solid ${accent}`, paddingLeft: 10 }}>
          {icon} {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const TooltipBRL = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <p style={{ fontWeight: 700, marginBottom: 4, fontSize: 12 }}>{label}</p>
      {payload.map((p, i) => {
        const name = (p.name || '').toLowerCase();
        const val  = typeof p.value === 'number'
          ? (name.includes('pedidos') || name.includes('cliques') || name.includes('sessões')
            ? fmtNum(p.value) : fmtBRL(p.value))
          : p.value;
        return <p key={i} style={{ color: p.color, margin: '2px 0', fontSize: 12 }}>{p.name}: {val}</p>;
      })}
    </div>
  );
};

function PeriodSelector({ periodIdx, setPeriodIdx, customFrom, setCustomFrom, customTo, setCustomTo,
  label = '', onApply, compact = false }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {label && <span style={{ fontSize: 12, color: C.textSoft, fontWeight: 600, marginRight: 4 }}>{label}</span>}
      {PERIODS.map((p, i) => (
        <button key={i} onClick={() => setPeriodIdx(i)}
          style={{ ...S.pill, ...(periodIdx === i ? (compact ? S.pillActiveCompare : S.pillActive) : {}),
            padding: compact ? '4px 10px' : '6px 14px', fontSize: compact ? 11 : 12 }}>
          {p.label}
        </button>
      ))}
      {periodIdx === 6 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6, width: '100%' }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={S.dateInput} />
          <span style={{ color: C.muted, fontSize: 12 }}>até</span>
          <input type="date" value={customTo}   onChange={e => setCustomTo(e.target.value)}   style={S.dateInput} />
          {onApply && <button onClick={onApply} style={S.btnApply}>Aplicar</button>}
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Relatorios() {
  // Período principal
  const [pIdx,   setPIdx]   = useState(3);
  const [pFrom,  setPFrom]  = useState(daysAgoBR(30));
  const [pTo,    setPTo]    = useState(todayBR());

  // Comparação
  const [cmpOn,  setCmpOn]  = useState(false);
  const [cIdx,   setCIdx]   = useState(3);
  const [cFrom,  setCFrom]  = useState(daysAgoBR(60));
  const [cTo,    setCTo]    = useState(daysAgoBR(31));

  // Dados principais
  const [resumo,    setResumo]    = useState(null);
  const [timeline,  setTimeline]  = useState([]);
  const [lucroTl,   setLucroTl]   = useState([]);
  const [statusBd,  setStatusBd]  = useState([]);
  const [produtos,  setProdutos]  = useState([]);
  const [meta,      setMeta]      = useState(null);

  // Dados de comparação
  const [cResumo,   setCResumo]   = useState(null);
  const [cTimeline, setCTimeline] = useState([]);

  // GA4
  const [ga4Status,  setGa4Status]  = useState(null);
  const [ga4Data,    setGa4Data]    = useState(null);
  const [ga4Funnel,  setGa4Funnel]  = useState(null);
  const [ga4Props,   setGa4Props]   = useState([]);
  const [ga4PropId,  setGa4PropId]  = useState('');

  // UI
  const [loading,      setLoading]      = useState(true);
  const [ga4Connecting,setGa4Connecting]= useState(false);
  const [error,        setError]        = useState(null);

  // Resolve range do período ativo
  const resolveRange = useCallback((idx, customF, customT) => {
    if (idx === 6) return { from: customF, to: customT };
    const p = PERIODS[idx];
    return { from: p.from(), to: p.to() };
  }, []);

  // Quando usuário muda período principal, auto-calcula comparação = período anterior equivalente
  useEffect(() => {
    const { from, to } = resolveRange(pIdx, pFrom, pTo);
    const prev = prevPeriod(from, to);
    setCFrom(prev.from);
    setCTo(prev.to);
    setCIdx(6); // personalizado
  }, [pIdx, pFrom, pTo, resolveRange]);

  const fetchMain = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { from, to } = resolveRange(pIdx, pFrom, pTo);
    const qs = `date_from=${from}&date_to=${to}`;
    try {
      const [rf, tl, lt, sb, pr, mt] = await Promise.all([
        api.get(`/api/relatorios/resumo-financeiro?${qs}`),
        api.get(`/api/relatorios/timeline?${qs}`),
        api.get(`/api/relatorios/lucro-timeline?${qs}`),
        api.get(`/api/relatorios/status-breakdown?${qs}`),
        api.get(`/api/relatorios/produtos?${qs}`),
        api.get(`/api/relatorios/meta?${qs}`)
      ]);
      setResumo(rf.data.data);
      setTimeline(tl.data.data || []);
      setLucroTl(lt.data.data || []);
      setStatusBd(sb.data.data || []);
      setProdutos(pr.data.data || []);
      setMeta(mt.data.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [resolveRange, pIdx, pFrom, pTo]);

  const fetchCompare = useCallback(async () => {
    if (!cmpOn) return;
    const { from, to } = resolveRange(cIdx, cFrom, cTo);
    const qs = `date_from=${from}&date_to=${to}`;
    try {
      const [rf, tl] = await Promise.all([
        api.get(`/api/relatorios/resumo-financeiro?${qs}`),
        api.get(`/api/relatorios/timeline?${qs}`)
      ]);
      setCResumo(rf.data.data);
      setCTimeline(tl.data.data || []);
    } catch {}
  }, [cmpOn, resolveRange, cIdx, cFrom, cTo]);

  const fetchGa4 = useCallback(async () => {
    if (!ga4Status?.connected) return;
    const { from, to } = resolveRange(pIdx, pFrom, pTo);
    const qs = `date_from=${from}&date_to=${to}`;
    try {
      const p = await api.get('/api/ga4/properties');
      const props = p.data.data || [];
      setGa4Props(props);
      if (props.length === 1 && !ga4PropId) setGa4PropId(props[0].id);

      const selectedPropertyId = ga4PropId || ga4Status?.property_id;
      if (!selectedPropertyId) {
        setGa4Data(null);
        setGa4Funnel(null);
        return;
      }

      const [d, f] = await Promise.all([
        api.get(`/api/ga4/data?${qs}`),
        api.get(`/api/ga4/funnel?${qs}`)
      ]);
      setGa4Data(d.data.data);
      setGa4Funnel(f.data.data);
    } catch (e) {
      setGa4Data({ error: e.response?.data?.error || e.message });
    }
  }, [ga4Status, resolveRange, pIdx, pFrom, pTo, ga4PropId]);

  const fetchGa4Status = useCallback(async () => {
    try {
      const r = await api.get('/api/ga4/status');
      setGa4Status(r.data);
      if (r.data.property_id) setGa4PropId(r.data.property_id);
    } catch {}
  }, []);

  useEffect(() => { fetchMain(); }, [fetchMain]);
  useEffect(() => { fetchCompare(); }, [fetchCompare]);
  useEffect(() => { fetchGa4Status(); }, [fetchGa4Status]);
  useEffect(() => { if (ga4Status?.connected) fetchGa4(); }, [ga4Status, fetchGa4]);

  useEffect(() => {
    if (window.location.search.includes('ga4_connected=1')) {
      fetchGa4Status();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchGa4Status]);

  const handleConnectGa4 = async () => {
    setGa4Connecting(true);
    try {
      const r = await api.get('/api/ga4/auth');
      window.location.href = r.data.auth_url;
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
      setGa4Connecting(false);
    }
  };

  const handleSaveProperty = async () => {
    if (!ga4PropId) return;
    await api.post('/api/ga4/property', { property_id: ga4PropId });
    await fetchGa4Status();
    await fetchGa4();
  };

  const handleDisconnectGa4 = async () => {
    if (!window.confirm('Desconectar GA4?')) return;
    await api.delete('/api/ga4/disconnect');
    setGa4Status({ connected: false });
    setGa4Data(null);
    setGa4Funnel(null);
  };

  // Monta dados do gráfico principal mesclando timeline atual e de comparação
  const mainChart = timeline.map((t, i) => {
    const lucroRow = lucroTl.find(l => l.dia === t.dia);
    const cRow = cTimeline[i];
    return {
      dia:       `Dia ${i + 1}`,
      receita:   t.receita || 0,
      lucro:     lucroRow?.lucro || 0,
      pedidos:   t.pedidos || 0,
      ...(cmpOn && cRow ? { receita_cmp: cRow.receita, pedidos_cmp: cRow.pedidos } : {})
    };
  });

  const d  = resumo?.atual || {};
  const dd = resumo?.delta || {};
  const c  = cResumo?.atual || {};

  const cmpLabel = cmpOn
    ? (() => { const { from, to } = resolveRange(cIdx, cFrom, cTo); return `${from} – ${to}`; })()
    : '';

  // Alertas inteligentes
  const alertas = [];
  if (d.roas != null && d.roas < 1.5) alertas.push({ txt: `ROAS baixo (${fmtRoas(d.roas)}) — cada R$1 em ads gera apenas R$${d.roas?.toFixed(2)} de receita`, cor: C.danger });
  if (cmpOn && c.receita && d.receita < c.receita * 0.85) alertas.push({ txt: `Receita ${Math.round(((d.receita - c.receita) / c.receita) * 100)}% comparado ao período anterior`, cor: C.danger });
  if (meta?.campaigns?.length > 0) {
    meta.campaigns.forEach(cam => {
      if (cam.ctr < 0.5 && cam.impressions > 1000) alertas.push({ txt: `Campanha "${cam.name}": CTR muito baixo (${fmtPct(cam.ctr)})`, cor: C.warning });
      if (cam.cpc > 5) alertas.push({ txt: `Campanha "${cam.name}": CPC alto (${fmtBRL(cam.cpc)}/clique)`, cor: C.warning });
    });
  }

  const { from: pFromVal, to: pToVal } = resolveRange(pIdx, pFrom, pTo);

  return (
    <div style={S.page}>
      {/* ── Cabeçalho ── */}
      <div style={S.header}>
        <div>
          <h2 style={S.pageTitle}>Relatórios & Insights</h2>
          <p style={S.pageSub}>{pFromVal} → {pToVal}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setCmpOn(v => !v)}
            style={{ ...S.btnRefresh, ...(cmpOn ? { background: C.compare, color: '#fff', borderColor: C.compare } : {}) }}>
            ⟷ {cmpOn ? 'Comparação ativa' : 'Comparar períodos'}
          </button>
          <button onClick={() => { fetchMain(); if (cmpOn) fetchCompare(); }} style={S.btnRefresh}>
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* ── Seletor de período ── */}
      <div style={S.periodBar}>
        <PeriodSelector periodIdx={pIdx} setPeriodIdx={setPIdx}
          customFrom={pFrom} setCustomFrom={setPFrom}
          customTo={pTo} setCustomTo={setPTo}
          onApply={() => { fetchMain(); if (cmpOn) fetchCompare(); }} />

        {cmpOn && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.compare}40` }}>
            <PeriodSelector periodIdx={cIdx} setPeriodIdx={setCIdx}
              customFrom={cFrom} setCustomFrom={setCFrom}
              customTo={cTo} setCustomTo={setCTo}
              label="Comparar com:" onApply={fetchCompare} compact />
          </div>
        )}
      </div>

      {error && <div style={S.errorBanner}>⚠ {error}</div>}

      {loading ? (
        <div style={S.loading}><div style={S.spinner} />Carregando dados…</div>
      ) : (
        <>
          {/* ── Alertas ── */}
          {alertas.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {alertas.map((a, i) => (
                <div key={i} style={{ ...S.alertRow, borderLeftColor: a.cor, background: a.cor + '10' }}>
                  <span style={{ color: a.cor, fontWeight: 600, fontSize: 13 }}>⚠ {a.txt}</span>
                </div>
              ))}
            </div>
          )}

          {/* ══ SEÇÃO 1 — FINANCEIRO ══ */}
          <Section title="Visão Financeira" icon="💰" accent={C.success}>
            <div style={S.kpiGrid}>
              <KpiCard title="Receita Total"    value={d.receita}         delta={cmpOn ? null : dd.receita}
                compareValue={cmpOn ? c.receita : null} compareLabel={cmpLabel}
                color={C.success} icon="💵"
                {...(cmpOn && c.receita ? { delta: Math.round(((d.receita - c.receita) / c.receita) * 100) } : {})} />
              <KpiCard title="Lucro Estimado"   value={d.lucro}           delta={cmpOn ? null : dd.lucro}
                compareValue={cmpOn ? c.lucro : null} compareLabel={cmpLabel}
                color={C.primary} icon="📈"
                {...(cmpOn && c.lucro ? { delta: Math.round(((d.lucro - c.lucro) / c.lucro) * 100) } : {})} />
              <KpiCard title="Ticket Médio"     value={d.ticket}          delta={cmpOn ? null : dd.ticket}
                compareValue={cmpOn ? c.ticket : null} compareLabel={cmpLabel}
                color={C.info} icon="🎯"
                {...(cmpOn && c.ticket ? { delta: Math.round(((d.ticket - c.ticket) / c.ticket) * 100) } : {})} />
              <KpiCard title="Total de Pedidos" value={d.pedidos}         delta={cmpOn ? null : dd.pedidos}
                compareValue={cmpOn ? c.pedidos : null} compareLabel={cmpLabel}
                formatter={fmtNum} color={C.purple} icon="📦"
                {...(cmpOn && c.pedidos ? { delta: Math.round(((d.pedidos - c.pedidos) / c.pedidos) * 100) } : {})} />
              {d.meta_connected && (
                <>
                  <KpiCard title="Gasto Ads (Meta)" value={d.ads_total}
                    subvalue={`líquido: ${fmtBRL(d.ads_spend)}`}
                    compareValue={cmpOn && c.ads_total ? c.ads_total : null} compareLabel={cmpLabel}
                    color={C.meta} icon="📣" />
                  <KpiCard title="Lucro após Ads" value={d.lucro_apos_ads}
                    color={d.lucro_apos_ads >= 0 ? C.success : C.danger}
                    compareValue={cmpOn && c.lucro_apos_ads != null ? c.lucro_apos_ads : null} compareLabel={cmpLabel}
                    icon="🏦" alert={d.lucro_apos_ads < 0} />
                  <KpiCard title="ROAS" value={d.roas} formatter={fmtRoas}
                    subvalue={`R$${d.roas?.toFixed(2)} por R$1 em ads`}
                    compareValue={cmpOn && c.roas != null ? c.roas : null} compareLabel={cmpLabel}
                    color={d.roas >= 2 ? C.success : d.roas >= 1 ? C.warning : C.danger}
                    icon="⚡" alert={d.roas != null && d.roas < 1.5} />
                </>
              )}
            </div>
          </Section>

          {/* ── Gráfico principal ── */}
          <div style={S.card}>
            <h4 style={S.chartTitle}>
              Receita, Lucro e Pedidos por Dia
              {cmpOn && <span style={{ fontSize: 11, color: C.compare, marginLeft: 10 }}>
                ⟷ linhas tracejadas = período de comparação
              </span>}
            </h4>
            {mainChart.length === 0 ? <p style={S.empty}>Sem dados no período.</p> : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={mainChart} margin={{ top: 5, right: 24, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.success} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.success} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                  <XAxis dataKey="dia" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={v => 'R$' + (v / 1000).toFixed(0) + 'k'} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip content={<TooltipBRL />} />
                  <Legend />
                  <Area yAxisId="l" type="monotone" dataKey="receita" name="Receita"
                    stroke={C.success} fill="url(#gRec)" strokeWidth={2} dot={false} />
                  <Line yAxisId="l" type="monotone" dataKey="lucro" name="Lucro"
                    stroke={C.primary} strokeWidth={2} dot={false} strokeDasharray="5 3" />
                  <Bar yAxisId="r" dataKey="pedidos" name="Pedidos"
                    fill={C.info + '60'} radius={[3, 3, 0, 0]} />
                  {cmpOn && mainChart.some(d => d.receita_cmp != null) && (
                    <Line yAxisId="l" type="monotone" dataKey="receita_cmp" name="Receita (comparação)"
                      stroke={C.compare} strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  )}
                  {cmpOn && mainChart.some(d => d.pedidos_cmp != null) && (
                    <Bar yAxisId="r" dataKey="pedidos_cmp" name="Pedidos (comparação)"
                      fill={C.compare + '50'} radius={[3, 3, 0, 0]} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ══ SEÇÃO 2 — META ADS ══ */}
          <Section title="Meta Ads — Campanhas" icon="📣" accent={C.meta}>
            {!meta?.connected ? (
              <p style={{ color: C.muted, textAlign: 'center', padding: 16 }}>
                Meta Ads não conectado. Configure na tela de Lucro.
              </p>
            ) : meta?.error ? (
              <div style={S.errorBanner}>{meta.error}</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {[
                    { l: 'Investimento',  v: fmtBRL(meta.totals?.spend),       c: C.meta   },
                    { l: 'Impressões',    v: fmtNum(meta.totals?.impressions),  c: C.purple },
                    { l: 'Cliques',       v: fmtNum(meta.totals?.clicks),       c: C.info   },
                    { l: 'CTR Médio',     v: fmtPct(meta.totals?.ctr),          c: meta.totals?.ctr > 1 ? C.success : C.warning },
                    { l: 'CPC Médio',     v: fmtBRL(meta.totals?.cpc),          c: meta.totals?.cpc < 2 ? C.success : meta.totals?.cpc < 5 ? C.warning : C.danger },
                    { l: 'CPM',           v: fmtBRL(meta.totals?.cpm),          c: C.textSoft },
                    { l: 'Alcance',       v: fmtNum(meta.totals?.reach),        c: C.pink   },
                  ].map((m, i) => (
                    <div key={i} style={{ ...S.metaKpi, borderTop: `2px solid ${m.c}` }}>
                      <p style={S.kpiLabel}>{m.l}</p>
                      <p style={{ fontWeight: 700, fontSize: 17, color: m.c, margin: 0 }}>{m.v}</p>
                    </div>
                  ))}
                </div>

                {meta.campaigns?.length > 0 && (
                  <>
                    <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                      <table style={S.table}>
                        <thead>
                          <tr>{['Campanha','Investimento','Impressões','Cliques','CTR','CPC','CPM','Compras'].map(h =>
                            <th key={h} style={S.th}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {meta.campaigns.sort((a, b) => b.spend - a.spend).map((cam, i) => (
                            <tr key={i} style={i % 2 === 0 ? { background: '#f8fafc' } : {}}>
                              <td style={{ ...S.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                  background: cam.ctr >= 1 ? C.success : cam.ctr >= 0.5 ? C.warning : C.danger,
                                  marginRight: 6 }} />
                                <span title={cam.name}>{cam.name}</span>
                              </td>
                              <td style={{ ...S.td, fontWeight: 600, color: C.meta }}>{fmtBRL(cam.spend)}</td>
                              <td style={S.td}>{fmtNum(cam.impressions)}</td>
                              <td style={S.td}>{fmtNum(cam.clicks)}</td>
                              <td style={{ ...S.td, fontWeight: 600, color: cam.ctr >= 1 ? C.success : cam.ctr >= 0.5 ? C.warning : C.danger }}>{fmtPct(cam.ctr)}</td>
                              <td style={{ ...S.td, fontWeight: 600, color: cam.cpc <= 2 ? C.success : cam.cpc <= 5 ? C.warning : C.danger }}>{fmtBRL(cam.cpc)}</td>
                              <td style={S.td}>{fmtBRL(cam.cpm)}</td>
                              <td style={S.td}>{cam.purchases > 0 ? <b style={{ color: C.success }}>{cam.purchases}</b> : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4 style={S.chartTitle}>Investimento por Campanha</h4>
                    <ResponsiveContainer width="100%" height={Math.max(180, meta.campaigns.length * 32)}>
                      <BarChart data={meta.campaigns.sort((a, b) => b.spend - a.spend).slice(0, 8).map(cam => ({
                        name: cam.name.length > 28 ? cam.name.slice(0, 28) + '…' : cam.name,
                        spend: cam.spend, clicks: cam.clicks
                      }))} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => 'R$' + v.toFixed(0)} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={170} />
                        <Tooltip content={<TooltipBRL />} />
                        <Bar dataKey="spend" name="Investimento" radius={[0, 4, 4, 0]}>
                          {meta.campaigns.slice(0, 8).map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </>
            )}
          </Section>

          {/* ══ SEÇÃO 3 — PEDIDOS / PRODUTOS ══ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginBottom: 24 }}>
            <div style={S.card}>
              <h4 style={S.chartTitle}>Pedidos por Status</h4>
              {statusBd.length === 0 ? <p style={S.empty}>Sem dados.</p> : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={statusBd} dataKey="total" nameKey="status"
                        cx="50%" cy="50%" outerRadius={75} innerRadius={42} paddingAngle={3}>
                        {statusBd.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} pedidos`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                    <tbody>
                      {statusBd.map((s, i) => (
                        <tr key={i}>
                          <td><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                            background: CHART_COLORS[i % CHART_COLORS.length], marginRight: 6 }} /></td>
                          <td style={{ fontSize: 12, color: C.text }}>{s.status}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{s.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>

            <div style={S.card}>
              <h4 style={S.chartTitle}>Top Produtos</h4>
              {produtos.length === 0 ? <p style={S.empty}>Sem dados.</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={produtos.slice(0, 7)} layout="vertical"
                    margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="produto" tick={{ fontSize: 10 }} width={120}
                      tickFormatter={v => v.length > 18 ? v.slice(0, 18) + '…' : v} />
                    <Tooltip />
                    <Bar dataKey="quantidade" name="Qtd vendida" radius={[0, 4, 4, 0]}>
                      {produtos.slice(0, 7).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ══ SEÇÃO 4 — GOOGLE ANALYTICS 4 ══ */}
          <Section title="Google Analytics 4" icon="📊" accent={C.info}>
            {!ga4Status?.connected ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ color: C.muted, marginBottom: 16 }}>
                  Conecte o GA4 para ver sessões, funil de conversão, canais e comportamento.
                </p>
                <button onClick={handleConnectGa4} disabled={ga4Connecting} style={S.btnConnectGa4}>
                  {ga4Connecting ? 'Redirecionando…' : '🔗 Conectar Google Analytics 4'}
                </button>
              </div>
            ) : (
              <>
                {/* Seletor de propriedade */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>✅ GA4 conectado</span>
                  {ga4Props.length > 0 ? (
                    <select value={ga4PropId} onChange={e => setGa4PropId(e.target.value)} style={S.dateInput}>
                      <option value="">Selecione…</option>
                      {ga4Props.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : (
                    <input type="text" placeholder="Property ID" value={ga4PropId}
                      onChange={e => setGa4PropId(e.target.value)} style={{ ...S.dateInput, width: 160 }} />
                  )}
                  <button onClick={handleSaveProperty} style={S.btnApply}>Usar esta</button>
                  <button onClick={handleDisconnectGa4}
                    style={{ ...S.btnApply, background: '#fff', color: C.danger, border: `1px solid ${C.danger}` }}>
                    Desconectar
                  </button>
                </div>

                {ga4Data?.error ? (
                  <div style={S.errorBanner}>{ga4Data.error}</div>
                ) : ga4Data ? (
                  <>
                    {/* KPIs GA4 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
                      {[
                        { l: 'Sessões',        v: fmtNum(ga4Data.kpis.sessions),             c: C.info    },
                        { l: 'Usuários',       v: fmtNum(ga4Data.kpis.total_users),           c: C.primary },
                        { l: 'Novos Usuários', v: fmtNum(ga4Data.kpis.new_users),             c: C.purple  },
                        { l: 'Bounce Rate',    v: fmtPct(ga4Data.kpis.bounce_rate),           c: ga4Data.kpis.bounce_rate < 0.5 ? C.success : C.warning },
                        { l: 'Duração Média',  v: `${Math.floor(ga4Data.kpis.avg_session_duration / 60)}m ${Math.round(ga4Data.kpis.avg_session_duration % 60)}s`, c: C.success },
                        { l: 'Conversões',     v: fmtNum(ga4Data.kpis.conversions),           c: C.pink    },
                      ].map((m, i) => (
                        <div key={i} style={{ ...S.metaKpi, borderTop: `2px solid ${m.c}` }}>
                          <p style={S.kpiLabel}>{m.l}</p>
                          <p style={{ fontWeight: 700, fontSize: 17, color: m.c, margin: 0 }}>{m.v}</p>
                        </div>
                      ))}
                    </div>

                    {/* Funil de conversão */}
                    {ga4Funnel && ga4Funnel.some(f => f.value > 0) && (
                      <div style={{ marginBottom: 24 }}>
                        <h4 style={S.chartTitle}>Funil de Conversão</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <FunnelChart>
                              <Tooltip
                                formatter={(value, name, props) => [fmtNum(props?.payload?.value ?? value), props?.payload?.name ?? name]}
                              />
                              <Funnel dataKey="value" data={ga4Funnel} isAnimationActive lastShapeType="rectangle">
                                {ga4Funnel.map((step, i) => (
                                  <Cell key={i} fill={step.fill} />
                                ))}
                                <LabelList
                                  dataKey="value"
                                  position="center"
                                  fill="#fff"
                                  fontSize={13}
                                  fontWeight={700}
                                  formatter={v => (v != null && !isNaN(Number(v)) && Number(v) > 0) ? Number(v).toLocaleString('pt-BR') : ''}
                                />
                              </Funnel>
                            </FunnelChart>
                          </ResponsiveContainer>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                            {ga4Funnel.map((step, i) => (
                              <div key={i} style={{ padding: '10px 14px', borderRadius: 8, background: '#f8fafc',
                                border: `1px solid ${step.fill}40`, borderLeft: `3px solid ${step.fill}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{step.name}</span>
                                  <span style={{ fontSize: 15, fontWeight: 800, color: step.fill }}>{fmtNum(step.value)}</span>
                                </div>
                                {step.rate_prev != null && (
                                  <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>
                                      da etapa anterior: <b style={{ color: step.rate_prev > 20 ? C.success : C.warning }}>{step.rate_prev}%</b>
                                    </span>
                                    <span style={{ fontSize: 11, color: C.muted }}>
                                      total: <b style={{ color: C.textSoft }}>{step.rate_overall}%</b>
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Canais + Dispositivos */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginBottom: 20 }}>
                      <div>
                        <h4 style={S.chartTitle}>Canais de Tráfego</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={ga4Data.channels} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                            <XAxis dataKey="canal" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="sessions" name="Sessões" fill={C.info} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div>
                        <h4 style={S.chartTitle}>Dispositivos</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={ga4Data.devices} dataKey="sessions" nameKey="dispositivo"
                              cx="50%" cy="50%" outerRadius={80}
                              label={({ dispositivo, percent }) => `${dispositivo} ${(percent * 100).toFixed(0)}%`}>
                              {ga4Data.devices.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={v => [fmtNum(v) + ' sessões']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Páginas mais visitadas */}
                    {ga4Data.pages?.length > 0 && (
                      <div style={{ overflowX: 'auto' }}>
                        <h4 style={S.chartTitle}>Páginas Mais Visitadas</h4>
                        <table style={S.table}>
                          <thead>
                            <tr>{['#', 'Página', 'Visualizações', 'Duração Média'].map(h =>
                              <th key={h} style={S.th}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {ga4Data.pages.map((p, i) => (
                              <tr key={i} style={i % 2 === 0 ? { background: '#f8fafc' } : {}}>
                                <td style={{ ...S.td, color: C.muted, width: 28 }}>{i + 1}</td>
                                <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, maxWidth: 300,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pagina}</td>
                                <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmtNum(p.pageviews)}</td>
                                <td style={{ ...S.td, textAlign: 'right', color: C.muted }}>
                                  {Math.floor(p.avg_duration / 60)}m {Math.round(p.avg_duration % 60)}s
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : ga4Status?.property_id ? (
                  <p style={{ color: C.muted, textAlign: 'center', padding: 20 }}>Carregando dados do GA4…</p>
                ) : (
                  <p style={{ color: C.warning, fontSize: 13 }}>Selecione a propriedade GA4 acima.</p>
                )}
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: {
    padding: '24px', maxWidth: 1280, margin: '0 auto',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: C.text
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 10
  },
  pageTitle: { fontSize: 22, fontWeight: 800, margin: 0 },
  pageSub:   { fontSize: 12, color: C.muted, margin: '3px 0 0', fontFamily: 'monospace' },
  card: {
    background: C.card, borderRadius: 12, padding: '18px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20, border: `1px solid ${C.border}`
  },
  section: {
    background: C.card, borderRadius: 12, padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 24, border: `1px solid ${C.border}`
  },
  sectionTitle: { fontSize: 15, fontWeight: 700, margin: 0 },
  chartTitle:   { fontSize: 13, fontWeight: 700, color: C.textSoft, margin: '0 0 12px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 12, marginBottom: 20 },
  kpiLabel: { fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase',
    letterSpacing: '0.06em', margin: '0 0 5px' },
  kpiValue: { fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.15 },
  kpiSub:   { fontSize: 11, color: C.muted, margin: '2px 0 0' },
  alertBadge: {
    position: 'absolute', top: 8, right: 10, fontSize: 9,
    background: C.danger + '18', color: C.danger, padding: '2px 6px', borderRadius: 10, fontWeight: 700
  },
  metaKpi: { background: '#f8fafc', borderRadius: 8, padding: '10px 12px', border: `1px solid ${C.border}` },
  periodBar: {
    background: C.card, borderRadius: 10, padding: '14px 16px', marginBottom: 20,
    border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  pill:             { padding: '6px 14px', borderRadius: 20, fontSize: 12, border: `1px solid ${C.border}`,
    background: '#f8fafc', cursor: 'pointer', fontWeight: 500, color: C.textSoft },
  pillActive:       { background: C.primary, color: '#fff', border: `1px solid ${C.primary}` },
  pillActiveCompare:{ background: C.compare, color: '#fff', border: `1px solid ${C.compare}` },
  dateInput:        { padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12 },
  btnApply:         { padding: '6px 14px', borderRadius: 8, background: C.primary, color: '#fff',
    border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnRefresh:       { padding: '8px 16px', borderRadius: 8, background: '#f8fafc',
    border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.textSoft },
  btnConnectGa4:    { padding: '11px 24px', borderRadius: 10, background: `linear-gradient(135deg, ${C.info}, #2563eb)`,
    color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
    boxShadow: '0 4px 12px rgba(59,130,246,0.35)' },
  table:            { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th:               { padding: '8px 12px', background: '#f8fafc', fontWeight: 700, fontSize: 11,
    color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: `1px solid ${C.border}`, textAlign: 'left' },
  td:               { padding: '9px 12px', borderBottom: `1px solid #f1f5f9`, color: C.text },
  tooltip:          { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '8px 12px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  errorBanner:      { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
    padding: '10px 14px', color: C.danger, fontSize: 13, marginBottom: 16 },
  alertRow:         { padding: '10px 14px', borderLeft: '4px solid', borderRadius: '0 8px 8px 0', marginBottom: 8 },
  loading:          { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0',
    gap: 16, color: C.muted, fontSize: 15 },
  spinner:          { width: 36, height: 36, border: `3px solid ${C.border}`, borderTopColor: C.primary,
    borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty:            { textAlign: 'center', color: C.muted, padding: '30px 0', fontSize: 13 },
};
